import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Phone, MessageCircle, MapPin, Scale, Award, ChevronRight, Clock, Shield, CheckCircle, ArrowRight } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

// City + Practice Area combinations
const cityPracticeData = {
  'seattle-car-accident-lawyer': {
    city: 'Seattle',
    practiceArea: 'Car Accident',
    practiceSlug: 'car-crashes',
    county: 'King County',
    description: 'Experienced Seattle car accident attorney helping victims recover compensation for injuries, lost wages, and pain and suffering. Free consultation.',
    localInfo: 'Seattle\'s busy I-5, I-90, and SR-99 corridors see thousands of accidents annually. From downtown collisions to highway pile-ups, I help Seattle residents navigate the complex claims process.',
    commonCauses: ['I-5 freeway accidents', 'Aurora Avenue (SR-99) collisions', 'Downtown Seattle intersections', 'Mercer Street corridor crashes', 'West Seattle Bridge area accidents']
  },
  'seattle-maritime-lawyer': {
    city: 'Seattle',
    practiceArea: 'Maritime Injury',
    practiceSlug: 'maritime-injuries',
    county: 'King County',
    description: 'Seattle maritime injury and Jones Act attorney representing injured seamen, fishermen, and dock workers. Serving the Port of Seattle and Puget Sound.',
    localInfo: 'Seattle is one of the busiest maritime hubs in the Pacific Northwest. The Port of Seattle, commercial fishing fleet, and ferry system create unique risks for maritime workers.',
    commonCauses: ['Port of Seattle dock injuries', 'Commercial fishing accidents', 'Ferry system incidents', 'Tugboat and barge accidents', 'Shipyard injuries']
  },
  'tacoma-car-accident-lawyer': {
    city: 'Tacoma',
    practiceArea: 'Car Accident',
    practiceSlug: 'car-crashes',
    county: 'Pierce County',
    description: 'Tacoma car accident lawyer with proven results. Helping Pierce County residents recover maximum compensation after auto collisions.',
    localInfo: 'Tacoma\'s I-5 and SR-16 corridors, along with the busy Pacific Avenue, see frequent serious accidents. I help Tacoma families get the compensation they deserve.',
    commonCauses: ['I-5 corridor accidents', 'SR-16 crashes', 'Pacific Avenue collisions', 'Port of Tacoma truck accidents', 'Stadium District pedestrian accidents']
  },
  'everett-car-accident-lawyer': {
    city: 'Everett',
    practiceArea: 'Car Accident',
    practiceSlug: 'car-crashes',
    county: 'Snohomish County',
    description: 'Everett car accident attorney based in Snohomish County. Local knowledge, personal attention, proven results for auto accident victims.',
    localInfo: 'As an Everett-based attorney, I understand the local roads and challenges. From I-5 to Broadway, I help Snohomish County residents navigate their car accident claims.',
    commonCauses: ['I-5/US-2 interchange accidents', 'Broadway corridor crashes', 'Boeing Freeway incidents', 'Mukilteo Speedway collisions', 'Evergreen Way accidents']
  },
  'bellevue-car-accident-lawyer': {
    city: 'Bellevue',
    practiceArea: 'Car Accident',
    practiceSlug: 'car-crashes',
    county: 'King County',
    description: 'Bellevue car accident lawyer serving the Eastside. Experienced representation for auto accident victims in Bellevue and surrounding areas.',
    localInfo: 'Bellevue\'s rapid growth has led to increased traffic and accidents. From I-405 to downtown, I help Bellevue residents recover fair compensation.',
    commonCauses: ['I-405 corridor accidents', 'SR-520 bridge crashes', 'Downtown Bellevue intersections', 'Bellevue Way collisions', 'Construction zone accidents']
  },
  'spokane-car-accident-lawyer': {
    city: 'Spokane',
    practiceArea: 'Car Accident',
    practiceSlug: 'car-crashes',
    county: 'Spokane County',
    description: 'Spokane car accident attorney representing Eastern Washington. Helping accident victims across Spokane County recover compensation.',
    localInfo: 'While based in Everett, I serve clients throughout Washington, including Spokane. I handle Spokane car accident cases with the same dedication and personal attention.',
    commonCauses: ['I-90 corridor accidents', 'Division Street crashes', 'North-South freeway incidents', 'Downtown Spokane collisions', 'Spokane Valley accidents']
  }
};

const CityPracticeAreaPage = () => {
  const { slug } = useParams();
  const pageData = cityPracticeData[slug];

  // Redirect to 404 if page doesn't exist
  if (!pageData) {
    return <Navigate to="/404" replace />;
  }

  const { city, practiceArea, practiceSlug, county, description, localInfo, commonCauses } = pageData;

  const whyChooseMe = [
    {
      title: 'Local Knowledge',
      description: `Understanding ${city} roads, courts, and insurance practices gives your case an edge.`
    },
    {
      title: 'Personal Attention',
      description: 'You work directly with me, not a paralegal. I personally handle every aspect of your case.'
    },
    {
      title: 'Proven Results',
      description: 'Over 10 years of experience winning fair compensation for injury victims.'
    },
    {
      title: 'No Fees Unless You Win',
      description: 'Contingency fee means you pay nothing upfront and nothing unless I recover for you.'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="city-practice-page">
      <SEOHead 
        title={`${city} ${practiceArea} Lawyer | Amy C. Brown Law`}
        description={description}
        localArea={{ city, county, state: 'WA' }}
        serviceData={{
          name: `${city} ${practiceArea} Legal Services`,
          description: description,
          category: practiceArea
        }}
      />

      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-secondary via-secondary to-slate-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-5"></div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Serving {city}, {county}
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {city} {practiceArea} Lawyer
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              {description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-teal-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-teal-600 transition-all shadow-lg"
              >
                <Phone className="w-5 h-5" />
                (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/10 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Local Info Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary mb-6">
                {practiceArea} Cases in {city}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                {localInfo}
              </p>
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="font-semibold text-secondary mb-4">Common {city} {practiceArea} Scenarios:</h3>
                <ul className="space-y-3">
                  {commonCauses.map((cause, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{cause}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {whyChooseMe.map((item, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-semibold text-secondary mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="flex items-center gap-3">
              <Award className="w-8 h-8 text-primary" />
              <span className="font-medium text-secondary">Top 10 Personal Injury Attorney 2025</span>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-teal-600" />
              <span className="font-medium text-secondary">10+ Years Experience</span>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-teal-600" />
              <span className="font-medium text-secondary">No Fee Unless You Win</span>
            </div>
          </div>
        </div>
      </section>

      {/* Learn More Links */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-2xl font-bold text-secondary text-center mb-8">
            Learn More About {practiceArea} Cases
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Link 
              to={`/${practiceSlug}`}
              className="bg-slate-50 hover:bg-teal-50 rounded-xl p-6 text-center group transition-colors"
            >
              <Scale className="w-8 h-8 text-teal-600 mx-auto mb-3" />
              <span className="font-medium text-secondary group-hover:text-teal-600 block">
                {practiceArea} Overview
              </span>
              <ChevronRight className="w-4 h-4 text-teal-600 mx-auto mt-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to="/compensation-estimator"
              className="bg-slate-50 hover:bg-teal-50 rounded-xl p-6 text-center group transition-colors"
            >
              <Scale className="w-8 h-8 text-teal-600 mx-auto mb-3" />
              <span className="font-medium text-secondary group-hover:text-teal-600 block">
                Compensation Calculator
              </span>
              <ChevronRight className="w-4 h-4 text-teal-600 mx-auto mt-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to="/faqs"
              className="bg-slate-50 hover:bg-teal-50 rounded-xl p-6 text-center group transition-colors"
            >
              <Scale className="w-8 h-8 text-teal-600 mx-auto mb-3" />
              <span className="font-medium text-secondary group-hover:text-teal-600 block">
                FAQs
              </span>
              <ChevronRight className="w-4 h-4 text-teal-600 mx-auto mt-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Free Consultation for {city} Residents
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            I personally respond to every inquiry within 1 business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-50 transition-all shadow-xl"
            >
              <Phone className="w-6 h-6" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-6 h-6" />
              Text (425) 675-1224
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPracticeAreaPage;
